import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { receitasAPI, definicoesAPI, clientesAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  FileText,
  MoreVertical,
  Printer,
  Tag,
  FileCheck,
  Eye
} from 'lucide-react';
import Swal from 'sweetalert2';
import { generateReceitaPDF, generateEtiquetaPDF, generateAtestadoPDF, printPDF } from '../utils/pdfGenerator';

export const Receitas = () => {
  const [receitas, setReceitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [definicoes, setDefinicoes] = useState(null);
  usePageTitle('Receitas');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [receitasRes, definicoesRes] = await Promise.all([
        receitasAPI.getAll(),
        definicoesAPI.get()
      ]);
      setReceitas(receitasRes.data);
      setDefinicoes(definicoesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível carregar as receitas.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (receita) => {
    const result = await Swal.fire({
      title: 'Eliminar Receita?',
      text: `Tem a certeza que deseja eliminar a receita "${receita.numero_receita}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Sim, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await receitasAPI.delete(receita.id);
        setReceitas(receitas.filter(r => r.id !== receita.id));
        Swal.fire({
          icon: 'success',
          title: 'Eliminada!',
          text: 'Receita removida com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting receita:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Não foi possível eliminar a receita.',
          confirmButtonColor: '#0D9488'
        });
      }
    }
  };

  const handlePrintReceita = async (receita) => {
    try {
      const clienteRes = await clientesAPI.getOne(receita.cliente_id);
      const doc = await generateReceitaPDF(receita, clienteRes.data, definicoes);
      printPDF(doc);
    } catch (error) {
      console.error('Error printing receita:', error);
      const doc = await generateReceitaPDF(receita, null, definicoes);
      printPDF(doc);
    }
  };

  const handlePrintEtiqueta = async (receita) => {
    try {
      const clienteRes = await clientesAPI.getOne(receita.cliente_id);
      const doc = generateEtiquetaPDF(receita, clienteRes.data, definicoes);
      printPDF(doc);
    } catch (error) {
      console.error('Error printing etiqueta:', error);
      const doc = generateEtiquetaPDF(receita, null, definicoes);
      printPDF(doc);
    }
  };

  const handlePrintAtestado = async (receita) => {
    try {
      const clienteRes = await clientesAPI.getOne(receita.cliente_id);
      const doc = await generateAtestadoPDF(receita, clienteRes.data, definicoes);
      printPDF(doc);
    } catch (error) {
      console.error('Error printing atestado:', error);
      const doc = await generateAtestadoPDF(receita, null, definicoes);
      printPDF(doc);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredReceitas = receitas.filter(receita =>
    receita.cliente_nome?.toLowerCase().includes(search.toLowerCase()) ||
    receita.numero_receita?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="receitas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Receitas</h1>
          <p className="text-slate-500 mt-1">Gestão de receitas médicas</p>
        </div>
        <Link to="/receitas/nova">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-receita-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nova Receita
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por cliente ou número da receita..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-receitas-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" />
            Lista de Receitas ({filteredReceitas.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">A carregar...</div>
          ) : filteredReceitas.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>{search ? 'Nenhuma receita encontrada' : 'Nenhuma receita registada'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">N.º Receita</TableHead>
                    <TableHead className="font-semibold text-slate-700">Cliente</TableHead>
                    <TableHead className="font-semibold text-slate-700">Produtos</TableHead>
                    <TableHead className="font-semibold text-slate-700">Data</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReceitas.map((receita) => (
                    <TableRow key={receita.id} className="hover:bg-slate-50" data-testid={`receita-row-${receita.id}`}>
                      <TableCell>
                        <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                          {receita.numero_receita}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {receita.cliente_nome}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {receita.produtos?.length || 0} produto(s)
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(receita.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/receitas/${receita.id}`)}
                            data-testid={`view-receita-${receita.id}`}
                          >
                            <Eye className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/receitas/${receita.id}/editar`)}
                            data-testid={`edit-receita-${receita.id}`}
                          >
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`print-menu-${receita.id}`}>
                                <MoreVertical className="h-4 w-4 text-slate-500" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handlePrintReceita(receita)} data-testid={`print-receita-${receita.id}`}>
                                <Printer className="h-4 w-4 mr-2" />
                                Imprimir Receita
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePrintEtiqueta(receita)} data-testid={`print-etiqueta-${receita.id}`}>
                                <Tag className="h-4 w-4 mr-2" />
                                Imprimir Etiqueta
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePrintAtestado(receita)} data-testid={`print-atestado-${receita.id}`}>
                                <FileCheck className="h-4 w-4 mr-2" />
                                Imprimir Atestado
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(receita)}
                                className="text-red-600"
                                data-testid={`delete-receita-${receita.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
